import { Widget, Connection } from '../types/index.js';
import { widgetRegistry } from './widgetRegistry.js';
import { canConnect } from './connectionValidation.js';

export interface BulkConnectionSuggestion {
  sourceWidget: Widget;
  targetWidget: Widget;
  connections: Array<{
    sourceOutputId: string;
    targetInputId: string;
    outputName: string;
    inputName: string;
  }>;
}

/**
 * Find all possible connections between two widgets
 */
export function findPossibleConnections(
  sourceWidget: Widget,
  targetWidget: Widget
): BulkConnectionSuggestion['connections'] {
  const connections: BulkConnectionSuggestion['connections'] = [];
  
  const sourceDef = widgetRegistry.get(sourceWidget.type);
  const targetDef = widgetRegistry.get(targetWidget.type);
  
  if (!sourceDef || !targetDef) return connections;
  
  // Check each output against each input
  for (const output of sourceDef.outputDefinitions) {
    for (const input of targetDef.inputDefinitions) {
      if (canConnect(output, input)) {
        // Check if input already has a connection
        const targetInput = targetWidget.inputs.find(i => i.id === input.id);
        if (!targetInput?.connected || input.acceptsMultiple) {
          connections.push({
            sourceOutputId: output.id,
            targetInputId: input.id,
            outputName: output.name,
            inputName: input.name,
          });
        }
      }
    }
  }
  
  return connections;
}

/**
 * Get bulk connection suggestions for selected widgets
 */
export function getBulkConnectionSuggestions(
  widgets: Widget[],
  selectedWidgetIds: string[]
): BulkConnectionSuggestion[] {
  const suggestions: BulkConnectionSuggestion[] = [];
  
  // If exactly 2 widgets selected, suggest connections between them
  if (selectedWidgetIds.length === 2) {
    const source = widgets.find(w => w.id === selectedWidgetIds[0]);
    const target = widgets.find(w => w.id === selectedWidgetIds[1]);
    
    if (source && target) {
      const connections = findPossibleConnections(source, target);
      if (connections.length > 0) {
        suggestions.push({ sourceWidget: source, targetWidget: target, connections });
      }
      
      // Also check reverse direction
      const reverseConnections = findPossibleConnections(target, source);
      if (reverseConnections.length > 0) {
        suggestions.push({ sourceWidget: target, targetWidget: source, connections: reverseConnections });
      }
    }
  }
  
  // For more widgets, find natural pipelines
  if (selectedWidgetIds.length > 2) {
    // Sort widgets by their natural flow (data sources first, displays last)
    const selectedWidgets = selectedWidgetIds
      .map(id => widgets.find(w => w.id === id))
      .filter((w): w is Widget => w !== undefined);
    
    const sortedWidgets = sortWidgetsByDataFlow(selectedWidgets);
    
    // Find connections in sequence
    for (let i = 0; i < sortedWidgets.length - 1; i++) {
      const source = sortedWidgets[i];
      const target = sortedWidgets[i + 1];
      const connections = findPossibleConnections(source, target);
      
      if (connections.length > 0) {
        suggestions.push({ sourceWidget: source, targetWidget: target, connections });
      }
    }
  }
  
  return suggestions;
}

/**
 * Sort widgets by their natural data flow order
 */
function sortWidgetsByDataFlow(widgets: Widget[]): Widget[] {
  return widgets.sort((a, b) => {
    const aDef = widgetRegistry.get(a.type);
    const bDef = widgetRegistry.get(b.type);
    
    if (!aDef || !bDef) return 0;
    
    // Widgets with no inputs come first (data sources)
    const aInputCount = aDef.inputDefinitions.length;
    const bInputCount = bDef.inputDefinitions.length;
    
    if (aInputCount === 0 && bInputCount > 0) return -1;
    if (bInputCount === 0 && aInputCount > 0) return 1;
    
    // Widgets with no outputs come last (displays)
    const aOutputCount = aDef.outputDefinitions.length;
    const bOutputCount = bDef.outputDefinitions.length;
    
    if (aOutputCount === 0 && bOutputCount > 0) return 1;
    if (bOutputCount === 0 && aOutputCount > 0) return -1;
    
    // Otherwise sort by input/output ratio
    const aRatio = aInputCount / (aOutputCount || 1);
    const bRatio = bInputCount / (bOutputCount || 1);
    
    return aRatio - bRatio;
  });
}

/**
 * Create connections from a bulk suggestion
 */
export function createBulkConnections(
  suggestion: BulkConnectionSuggestion,
  createConnection: (connection: Omit<Connection, 'id'>) => void
): void {
  for (const conn of suggestion.connections) {
    createConnection({
      source: {
        widgetId: suggestion.sourceWidget.id,
        outputId: conn.sourceOutputId,
      },
      target: {
        widgetId: suggestion.targetWidget.id,
        inputId: conn.targetInputId,
      },
    });
  }
}